import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Drawer from '../../ui/Drawer'
import Button from '../../ui/Button'
import EmptyState from '../../ui/EmptyState'
import SearchInput from '../../ui/SearchInput'
import { alertNoun } from './constants'
import StatusProgressRow from './StatusProgressRow'

const FOOTER_CLASS = 'tw:flex! tw:items-center tw:justify-end tw:gap-2 tw:p-4 '
  + 'tw:border-t tw:border-solid tw:border-gray-300'
const TITLE_CLASS = 'tw:m-0! tw:text-base! tw:font-semibold! tw:text-gray-900! tw:leading-none!'
const HEADING_CLASS = 'tw:flex tw:items-baseline tw:gap-2 tw:flex-1 tw:min-w-0'
const EMPTY_MESSAGE = 'Aucun statut de cette catégorie ne correspond à votre recherche.'

const normalize = value => value.toLocaleLowerCase('fr-FR')

const CategoryStatusesDrawer = ({ isOpen, label, statuses, total, onClose }) => {
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!isOpen)
      setSearch('')
  }, [isOpen])

  const handleSearchChange = useCallback(event => setSearch(event.target.value), [])

  const matchingStatuses = useMemo(() => {
    const needle = normalize(search.trim())
    if (!needle)
      return statuses
    return statuses.filter(status => normalize(status.name).includes(needle))
  }, [statuses, search])

  const renderStatuses = () => {
    if (matchingStatuses.length === 0)
      return <EmptyState message={EMPTY_MESSAGE} title="Aucun statut" />
    return (
      <div className="tw:flex tw:flex-col tw:gap-2" role="list">
        {matchingStatuses.map(status => (
          <StatusProgressRow
            key={status.id}
            color={status.color}
            count={status.count}
            name={status.name}
            size="md"
            total={total}
          />
        ))}
      </div>
    )
  }

  return (
    <Drawer ariaLabel="Détail des statuts" isOpen={isOpen} onClose={onClose}>
      <Drawer.Header closeVariant="dismiss" onClose={onClose}>
        <div className={HEADING_CLASS}>
          <h2 className={TITLE_CLASS}>{label}</h2>
          <span className="tw:text-sm tw:text-gray-500">
            {`${total} ${alertNoun(total)}`}
          </span>
        </div>
      </Drawer.Header>
      <Drawer.Body>
        <SearchInput
          name="category-statuses-search"
          placeholder="Rechercher un statut..."
          value={search}
          onChange={handleSearchChange}
        />
        {renderStatuses()}
      </Drawer.Body>
      <footer className={FOOTER_CLASS}>
        <Button variant="neutral" onClick={onClose}>Fermer</Button>
      </footer>
    </Drawer>
  )
}

CategoryStatusesDrawer.propTypes = {
  isOpen: PropTypes.bool,
  label: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    count: PropTypes.number,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string
  })),
  total: PropTypes.number
}

CategoryStatusesDrawer.defaultProps = {
  isOpen: false,
  label: '',
  statuses: [],
  total: 0
}

export default CategoryStatusesDrawer
